export type ChangeType = 'quarantineFile' | 'quarantineFolder' | 'renameFile' | 'mergeFolders' | 'moveFolder' | 'flattenFolders' | 'fileIntake' | 'restructure';

export type ChangeStatus = 'pending' | 'approved' | 'rejected';

export type ConfidenceBucket = 'high' | 'medium' | 'low' | 'needsInput';

/**
 * One proposed change, as generated. Decisions and admin edits live in IChangeDecision,
 * so regenerating a plan never mutates what the admin already reviewed.
 */
export interface IPlannedChange {
  /** Stable within a scan: '{type}:{target id}'. */
  id: string;
  type: ChangeType;
  itemKind: 'file' | 'folder';
  /** The file or folder acted on. For merges, the folder everything is merged into. */
  targetId: string;
  /** Merges only: the folders whose contents move into the target. */
  sourceFolderIds?: string[];
  /** Merges only: every folder in the similar group, so the admin can pick a different target. */
  candidateFolderIds?: string[];
  /** Renames: suggested new file name. Undefined when the admin must supply one. */
  suggestedName?: string;
  /**
   * flattenFolders: pass-through folders to remove, shallowest first. Each one's single
   * subfolder moves up into its parent, and the emptied folder goes to quarantine.
   */
  collapseFolderIds?: string[];
  /** flattenFolders: deepest folder path before and after, for display. */
  flattenFrom?: string;
  flattenTo?: string;
  /** moveFolder / fileIntake / restructure: suggested destination folder path ('' = library root). */
  suggestedParentPath?: string;
  /** True when the change cannot be approved until the admin fills something in. */
  needsInput: boolean;
  reason: string;
  /** 0–1. Deterministic rules use fixed values per rule; AI suggestions will supply their own. */
  confidence: number;
  issueIds: string[];
}

export interface IChangeDecision {
  status: ChangeStatus;
  /** Admin-edited file name (renames). */
  newName?: string;
  /** Admin-edited parent path (folder moves). */
  parentPath?: string;
  /** Admin-chosen merge target (merges). */
  targetFolderId?: string;
  /** Where an edit came from, shown on the card — e.g. an AI suggestion's reason. */
  note?: string;
}

export interface IOrganizationPlan {
  changes: IPlannedChange[];
  /** Root folder for quarantined items, e.g. '_Quarantine/2026-09-27'. */
  quarantinePath: string;
}

export type PreviewMark = 'moved' | 'renamed' | 'quarantined' | 'merged';

export interface IPreviewEntry {
  id: string;
  kind: 'file' | 'folder';
  beforePath: string;
  afterPath: string;
  mark?: PreviewMark;
}

export interface IPlanConflict {
  changeIds: string[];
  message: string;
}

export interface IPlanPreview {
  entries: IPreviewEntry[];
  conflicts: IPlanConflict[];
}

export function confidenceBucket(change: IPlannedChange): ConfidenceBucket {
  if (change.needsInput) {
    return 'needsInput';
  }
  if (change.confidence >= 0.9) {
    return 'high';
  }
  return change.confidence >= 0.7 ? 'medium' : 'low';
}
